import moment from "moment";
import { Card } from "shared/components/Card";
import {
  ApplicationStatus,
  Invitee,
  VerificationNames,
  ApplicantType,
  DataVendors
} from "shared/types";
import { Cell } from "./components/Cell";
import { ApplicantOrInvitee, Verification } from "./types";
import { ApplicantHeaderCell } from "./components/ApplicantHeaderCell";
import { Applicant } from "types/application";
import { VerificationBadge } from "./components/TableBodySections/VerificationBadgeCell";
import { Fragment, useEffect, useMemo, useState } from "react";
import { WithIcon } from "./components/WithIcon";
import {
  HIDDEN_VERIFICATIONS,
  VERIFICATION_ICON,
  VERIFICATIONS_ORDER
} from "./constants";
import {
  getAvailableVerificationNames,
  sortVerifications,
  useApplicantsTableScroll,
  verificationNameToTitle
} from "./utils";
import { Show, Button, ButtonVariant, ButtonSize } from "shared/components";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { ChangeRoleModal } from "shared/app/ChangeRoleModal";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { combineVerifications, cx } from "shared/utils";
import { TableBody } from "views/applications-id/components/ApplicantsTable/components/TableBody";
import { ArrowRight, Attachment02 } from "untitledui-js-base";
import { RefundModal } from "../../../../shared/app/RefundModal";
import { CLEARA_CATEGORIZATION_SCHEMA_VERSION } from "views/applications-id-report/utils/constants";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";
import { RequiresOverridesIdentityVerification } from "shared/types";
import { getApplicantDisplayName } from "shared/utils/get-applicant-display-name";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { useSearchParams } from "react-router-dom";
import { ActiveDiscountsModal } from "views/discounts/components/ActiveDiscountsModal";
import { useDiscounts } from "views/discounts/hooks/useDiscounts";
import { UncancelModal } from "shared/app/UncancelModal";
import { ResendInviteModal } from "shared/app/ResendInviteModal";

import { DiscountTag02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { CriminalReport, VerificationReport } from "types/landlord/reports";

const magicLinkMatchesApplicantByName = (
  magicLink: Invitee,
  applicant: ApplicantOrInvitee
) => {
  const magicFirst =
    "first_name" in magicLink ? (magicLink.first_name ?? undefined) : undefined;
  const magicLast =
    "last_name" in magicLink ? (magicLink.last_name ?? undefined) : undefined;
  const applicantFirst =
    "first_name" in applicant ? (applicant.first_name ?? undefined) : undefined;
  const applicantLast =
    "last_name" in applicant ? (applicant.last_name ?? undefined) : undefined;

  return Boolean(
    magicFirst &&
    magicLast &&
    applicantFirst &&
    applicantLast &&
    magicFirst === applicantFirst &&
    magicLast === applicantLast
  );
};

type ApplicantsTableProps = {
  availableRoles: ApplicantType[];
  applicationApplicants: Applicant[];
  applicants: ApplicantOrInvitee[];
  applicationId?: string;
  verificationSchemas?: VerificationSchemaNames;
  invalidateQueries?: unknown[];
  refunded?: boolean;
  scoreBased?: boolean;
  ruleBased?: boolean;
  applicationStatus?: ApplicationStatus;
  onChangeRole: () => void;
  allowCoSigner: boolean;
  allowOccupant: boolean;
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  pendingMagicLinks: Invitee[];
  templateId?: string;
};

export const ApplicantsTable = ({
  availableRoles,
  applicationApplicants,
  applicants,
  applicationId,
  verificationSchemas,
  invalidateQueries,
  applicationStatus,
  ruleBased,
  onChangeRole,
  scoreBased,
  allowCoSigner,
  allowOccupant,
  requiresOverridesIdentityVerification,
  pendingMagicLinks,
  templateId
}: ApplicantsTableProps) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const paramsApplicantId = searchParams.get("applicantId");

  const [manualApplicationOpen, setManualApplicationOpen] = useState<
    number | null
  >(null);
  const [cancelApplicantModal, setCancelApplicantModal] = useState<
    undefined | string
  >();
  const [uncancelApplicantModal, setUncancelApplicantModal] = useState<
    undefined | string
  >();
  const [refundModal, setRefundModal] = useState<undefined | string>();
  const [resendInviteModal, setResendInviteModal] = useState<
    undefined | Invitee
  >();

  const [changeRoleModal, setChangeRoleModal] = useState<
    undefined | ApplicantOrInvitee
  >();

  const [showActiveDiscountCodes, setShowActiveDiscountCodes] = useState(false);

  const { data: availableDiscounts = [], refetch: fetchActiveDiscounts } =
    useDiscounts({
      active: true,
      restriction_type: "template",
      template_id: templateId,
      enabled: false
    });

  const {
    tableHeaderRef,
    handleScrollRight,
    showMoreButton,
    showStickyHeader,
    firstCellRef,
    viewportRef,
    scrollbarRef
  } = useApplicantsTableScroll();

  const { mutate: changeRole } = useMutation({
    mutationKey: ["CHANGE_ROLE", applicationId],
    mutationFn: (role: ApplicantType) =>
      instance
        .post(
          `/portal/v1/applications/${applicationId}/applicants/${changeRoleModal?.id}/change_role`,
          {
            role: role
          }
        )
        .then(() => {
          toast.success("Role updated successfully");
          setChangeRoleModal(undefined);

          if (invalidateQueries)
            queryClient.invalidateQueries({
              queryKey: invalidateQueries
            });

          onChangeRole();
        })
        .catch((err) => {
          toast.error(getErrorMessage(err));
          setChangeRoleModal(undefined);
        })
  });

  const availableVerificationNames = useMemo(() => {
    return getAvailableVerificationNames(applicants, verificationSchemas);
  }, [applicants, verificationSchemas]);

  useEffect(() => {
    if (paramsApplicantId) setManualApplicationOpen(Number(paramsApplicantId));
  }, [paramsApplicantId]);

  if (!applicants.length) return null;

  /*
    we might have issues with applicants/co signers using different vendors for criminal
    we don't support more categories for CIC, and in the long term, we will disable
    so I think we will be fine, just pointed out
  */
  const hasClearaCategorization = applicants.some(
    (applicant) =>
      "verifications" in applicant &&
      applicant.verifications.some((verification) => {
        if (verification.name !== VerificationNames.CriminalReport)
          return false;

        // Typecasting here because, because verification type is pretty primitive
        // I cast to probably overkill type but it fits the purpose 90%. Maybe
        // this even is exactly what type is, but not 100% safe. I don't want to
        // create a new concat for this.
        const criminalVerifications =
          verification as unknown as VerificationReport<CriminalReport>;

        return Boolean(
          criminalVerifications.vendor === DataVendors.Cleara &&
          criminalVerifications.schema?.version &&
          criminalVerifications.schema.version >=
            CLEARA_CATEGORIZATION_SCHEMA_VERSION
        );
      })
  );

  return (
    <>
      <Show if={Boolean(cancelApplicantModal)}>
        <CancelOrRefundModal
          onClose={() => {
            setCancelApplicantModal(undefined);
          }}
          onSuccess={() => {
            if (invalidateQueries)
              queryClient.invalidateQueries({
                queryKey: invalidateQueries
              });

            setCancelApplicantModal(undefined);
          }}
          applicationId={String(applicationId)}
          applicantId={String(cancelApplicantModal)}
        />
      </Show>
      <Show if={Boolean(uncancelApplicantModal)}>
        <UncancelModal
          onClose={() => {
            setUncancelApplicantModal(undefined);
          }}
          onSuccess={() => {
            if (invalidateQueries)
              queryClient.invalidateQueries({
                queryKey: invalidateQueries
              });

            setUncancelApplicantModal(undefined);
          }}
          applicationId={String(applicationId)}
          applicantId={String(uncancelApplicantModal)}
        />
      </Show>
      <Show if={Boolean(changeRoleModal)}>
        <ChangeRoleModal
          // Shows list of all available roles in template
          // I guess that's fine, since likely partners invited applicant
          // Realized they are guarantor. Want to change role back to guarantor
          // And only then invite applicant
          availableRoles={availableRoles}
          onClose={() => setChangeRoleModal(undefined)}
          onChange={(selectedRole) => {
            changeRole(selectedRole);
          }}
          applicantsList={applicationApplicants}
          isOpen={Boolean(changeRoleModal)}
          applicant={changeRoleModal}
        />
      </Show>
      <Show if={Boolean(refundModal)}>
        <RefundModal
          onClose={() => setRefundModal(undefined)}
          applicationId={String(applicationId)}
          applicantId={String(refundModal)}
          queryKey={invalidateQueries}
        />
      </Show>
      <Show if={Boolean(resendInviteModal)}>
        <ResendInviteModal
          onClose={() => setResendInviteModal(undefined)}
          onSuccess={() => {
            if (invalidateQueries)
              queryClient.invalidateQueries({
                queryKey: invalidateQueries
              });

            setResendInviteModal(undefined);
          }}
          pendingMagicLink={resendInviteModal as Invitee}
          applicationId={String(applicationId)}
        />
      </Show>
      <div>
        <Card className="relative max-w-[calc(100vw-128px)] rounded-none rounded-t-lg">
          {/* This sticky header appears only when scrolling down and the main header below is obscured */}
          <div
            className={cx(
              "sticky top-0 translate-y-full h-0 z-[11]",
              showStickyHeader ? "" : "hidden"
            )}
          >
            <div
              ref={tableHeaderRef}
              className="w-full hidden md:grid relative overflow-x-auto hide-scroll  border-b border-alpha-40"
              style={{
                gridTemplateColumns:
                  applicants.length > 1
                    ? `300px repeat(${applicants.length - 1}, 340px) minmax(340px, 1fr)`
                    : `300px minmax(340px, 1fr)`
              }}
            >
              <div className="bg-background-900 sticky left-0 frozen-column">
                <Cell isFirst />
              </div>

              {applicants.map((applicant) => (
                <Cell key={applicant.id} className="bg-background-900">
                  <h3 className="text-sm font-bold text-content-1000 truncate max-w-[320px]">
                    {getApplicantDisplayName(applicant)}
                  </h3>
                </Cell>
              ))}
            </div>
          </div>

          <div
            ref={viewportRef}
            className="w-full hidden md:grid relative overflow-x-auto hide-scroll rounded-lg"
            style={{
              gridTemplateColumns:
                applicants.length > 1
                  ? `300px repeat(${applicants.length - 1}, 340px) minmax(340px, 1fr)`
                  : `300px minmax(340px, 1fr)`
            }}
          >
            {/* --- HEADER --- */}
            <div className="sticky left-0 bg-white divide-y divide-alpha-40 frozen-column">
              {/*This is a marker cell. The sticky header above only appears when scrolling down and this cell becomes obscured*/}
              <div ref={firstCellRef}>
                <Cell className="!h-[176px]" />
              </div>

              <Cell isFirst isDataCell>
                Application status
              </Cell>

              <Cell isFirst isDataCell>
                Started
              </Cell>

              <Cell isFirst isDataCell>
                Submitted
              </Cell>

              <Cell isFirst isDataCell>
                <div className="flex items-center justify-between w-full">
                  <span>Discount</span>
                  {availableDiscounts.length > 0 ? (
                    <Button
                      variant={ButtonVariant.TEXT}
                      size={ButtonSize.SM}
                      HugeIcon={DiscountTag02Icon}
                      className="flex items-center gap-2 text-primary-500 hover:text-primary-600"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowActiveDiscountCodes(true);
                        fetchActiveDiscounts();
                      }}
                    >
                      View active codes
                    </Button>
                  ) : null}
                </div>
              </Cell>

              <Cell isFirst isDataCell>
                Total fee
              </Cell>

              {combineVerifications(
                sortVerifications(
                  availableVerificationNames.map((name) => ({
                    id: name,
                    name: name as VerificationNames
                  })) as unknown[] as Verification[],
                  VERIFICATIONS_ORDER
                ),
                [
                  [
                    VerificationNames.IncomeVerification,
                    VerificationNames.VerifiedIncomeVerification,
                    VerificationNames.CombinedIncome
                  ]
                ]
              ).map((v) => {
                if (Array.isArray(v))
                  return (
                    <Fragment key={v[0].id}>
                      <Cell className="bg-background-900">
                        <WithIcon
                          Icon={
                            VERIFICATION_ICON[
                              VerificationNames.IncomeVerification
                            ]
                          }
                          className="stroke-content-100"
                        >
                          <p className="text-content-300">Income history</p>
                        </WithIcon>
                      </Cell>

                      <Cell isDataCell>
                        <p>
                          Gross income
                          <span className="text-content-100"> (Verified)</span>
                        </p>
                      </Cell>
                      <Cell isDataCell>
                        <p>
                          Net income
                          <span className="text-content-100"> (Verified)</span>
                        </p>
                      </Cell>
                      <Cell isDataCell>
                        <p>
                          Gross income
                          <span className="text-content-100">
                            {" "}
                            (Self-reported)
                          </span>
                        </p>
                      </Cell>
                    </Fragment>
                  );
                else {
                  if (v.name === VerificationNames.RentalApplication)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                            className="stroke-content-100"
                          >
                            <p className="text-content-300">
                              {verificationNameToTitle(
                                v.name as VerificationNames
                              )}
                            </p>
                          </WithIcon>
                        </Cell>

                        <Cell isDataCell>Dependents</Cell>
                        <Cell isDataCell>Vehicles</Cell>
                        <Cell isDataCell>Pets or service animals</Cell>
                      </Fragment>
                    );

                  if (v.name === VerificationNames.HousingHistory)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                          >
                            {verificationNameToTitle(
                              v.name as VerificationNames
                            )}
                          </WithIcon>
                        </Cell>

                        <Cell isDataCell>Number of residences</Cell>
                        <Cell isDataCell>Average monthly payments</Cell>
                        <Cell isDataCell>Total months of residence</Cell>
                        {verificationSchemas?.rental_verification ? (
                          <Cell isDataCell>Landlord verification</Cell>
                        ) : null}
                      </Fragment>
                    );

                  if (v.name === VerificationNames.PetScreeningReport)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                          >
                            {verificationNameToTitle(
                              v.name as VerificationNames
                            )}
                          </WithIcon>
                        </Cell>

                        <Cell isDataCell>Pets / Assistance animals</Cell>
                      </Fragment>
                    );

                  if (v.name === VerificationNames.IdentityVerification)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                            className="stroke-content-100"
                          >
                            <p className="text-content-300">
                              {verificationNameToTitle(
                                v.name as VerificationNames
                              )}
                            </p>
                          </WithIcon>
                        </Cell>

                        <Cell isDataCell>Identity verification</Cell>
                      </Fragment>
                    );

                  if (v.name === VerificationNames.CreditReport)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                          >
                            {verificationNameToTitle(
                              v.name as VerificationNames
                            )}
                          </WithIcon>
                        </Cell>
                        <Cell
                          isDataCell
                          className="flex items-center pl-0 pr-0 relative"
                          isFirst
                        >
                          Credit score
                        </Cell>
                        <Cell
                          isDataCell
                          className="flex items-center pl-0 pr-0 relative"
                        >
                          Past due accounts
                        </Cell>
                        <Cell
                          isDataCell
                          className="flex items-center pl-0 pr-0 relative"
                        >
                          Balance past due
                        </Cell>
                        <Cell
                          isDataCell
                          className="flex items-center pl-0 pr-0 relative"
                        >
                          Balance of collections
                        </Cell>
                        <Cell
                          isDataCell
                          className="flex items-center pl-0 pr-0 relative"
                        >
                          Bankruptcy
                        </Cell>
                      </Fragment>
                    );

                  if (v.name === VerificationNames.EvictionReport)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                          >
                            {verificationNameToTitle(
                              v.name as VerificationNames
                            )}
                          </WithIcon>
                        </Cell>
                        <Cell
                          isDataCell
                          className="flex items-center pl-0 pr-0 relative"
                          isFirst
                        >
                          Events reported
                        </Cell>
                      </Fragment>
                    );

                  if (v.name === VerificationNames.CriminalReport)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                          >
                            {verificationNameToTitle(
                              v.name as VerificationNames
                            )}
                          </WithIcon>
                        </Cell>
                        {hasClearaCategorization ? (
                          <>
                            <Cell isDataCell>Felony</Cell>
                            <Cell isDataCell>Misdemeanor</Cell>
                            <Cell isDataCell>Other criminal</Cell>
                          </>
                        ) : (
                          <>
                            <Cell isDataCell>Criminal</Cell>
                            <Cell isDataCell>Criminal traffic</Cell>
                          </>
                        )}

                        <Cell isDataCell>National sex offender </Cell>
                        <Cell isDataCell>OFAC</Cell>
                      </Fragment>
                    );

                  if (v.name === VerificationNames.CanadianCriminalReport)
                    return (
                      <Fragment key={v.id}>
                        <Cell className="bg-background-900">
                          <WithIcon
                            Icon={
                              VERIFICATION_ICON[v.name as VerificationNames]
                            }
                          >
                            {verificationNameToTitle(
                              v.name as VerificationNames
                            )}
                          </WithIcon>
                        </Cell>

                        <Cell isDataCell>Report</Cell>
                      </Fragment>
                    );

                  return null;
                }
              })}

              <Cell className="bg-background-900">
                <WithIcon Icon={Attachment02}>Additional documents</WithIcon>
              </Cell>
              <Cell isDataCell>Documents uploaded</Cell>
            </div>
            {/* ---- BODY ---- */}
            <TableBody
              allowCoSigner={allowCoSigner}
              allowOccupant={allowOccupant}
              applicants={applicants}
              verificationNames={availableVerificationNames}
              applicationStatus={applicationStatus}
              applicationId={applicationId}
              templateId={templateId}
              pendingMagicLinks={pendingMagicLinks}
              ruleBased={ruleBased}
              scoreBased={scoreBased}
              hasClearaCategorization={hasClearaCategorization}
              setCancelApplicantModal={setCancelApplicantModal}
              setUncancelApplicantModal={setUncancelApplicantModal}
              setChangeRoleModal={setChangeRoleModal}
              setRefundModal={setRefundModal}
              setResendInviteModal={setResendInviteModal}
              verificationSchemas={verificationSchemas}
              requiresOverridesIdentityVerification={
                requiresOverridesIdentityVerification
              }
              setManualApplicationOpen={(v) => {
                setManualApplicationOpen(Number(v));
              }}
            />
          </div>
          {/*The blur effect and the 'More' button on the right side of this table appear when horizontal scrolling is possible*/}
          <Show if={showMoreButton}>
            <div className="absolute inset-y-0 w-32 cursor-pointer right-0 bg-gradient-to-l from-white via-white to-transparent z-[12] rounded-r-lg" />
            <div className="sticky bottom-[135px] -translate-y-full h-0 z-[13]">
              <Button
                onClick={handleScrollRight}
                className="ml-auto rounded-r-none border-r-0 text-content-400 text-sm h-[52px] !p-4 !max-h-[52px] shadow-xl"
                variant={ButtonVariant.BASE}
                size={ButtonSize.BASE}
                Icon={ArrowRight}
                iconPosition="right"
              >
                More
              </Button>
            </div>
          </Show>
        </Card>

        {/*sticky custom scrollbar*/}
        <div className="hidden md:block sticky bottom-0 z-[12] bg-white p-4 max-w-[calc(100vw-128px)] border-t border-alpha-100">
          <div
            ref={scrollbarRef}
            className="overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-alpha-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-alpha-200"
          >
            <div
              className="h-px"
              style={{
                width: 340 * applicants.length + 268
              }}
            />
          </div>
        </div>
      </div>

      <div className="md:hidden flex flex-wrap gap-4">
        {applicants.map((applicant) => {
          const isApplicant = "verifications" in applicant;
          return (
            <Card
              key={applicant.email + applicant.id}
              className="max-w-[280px] w-full"
              linkTo={
                "id" in applicant && isApplicant
                  ? `/applications/${applicationId}/reports?applicantId=${applicant.id}`
                  : undefined
              }
            >
              <ApplicantHeaderCell
                setManualApplicationOpen={(v: number) => {
                  setManualApplicationOpen(Number(v));
                }}
                allowCoSigner={allowCoSigner}
                allowOccupant={allowOccupant}
                setCancelApplicantModal={setCancelApplicantModal}
                setUncancelApplicantModal={setUncancelApplicantModal}
                setChangeRoleModal={setChangeRoleModal}
                setRefundModal={setRefundModal}
                applicant={applicant}
                applicationStatus={applicationStatus}
                applicationId={applicationId}
                // the collision of IDs is possible when link and applicant have the same ID. We assume that there > links than applicants.
                pendingMagicLink={pendingMagicLinks.find(
                  (link) => link.id === applicant.id
                )}
                key={applicant.id}
              />
              <div className="p-4">
                <p className="text-sm text-content-500">
                  Started at:{" "}
                  <span className="text-xs text-content-500 font-bold">
                    {isApplicant && applicant.application_started_at
                      ? moment(applicant.application_started_at).format("lll")
                      : "-"}
                  </span>
                </p>
                <p className="text-sm text-content-500">
                  Finished at:{" "}
                  <span className="text-xs text-content-500 font-bold">
                    {isApplicant && applicant.application_finished_at
                      ? moment(applicant.application_finished_at).format("lll")
                      : "-"}
                  </span>
                </p>

                <div className="h-px my-3 bg-alpha-100" />

                <div className="divide-y divide-alpha-80">
                  {"verifications" in applicant
                    ? applicant.verifications
                        ?.filter((d) => {
                          return !HIDDEN_VERIFICATIONS.includes(
                            d.name as VerificationNames
                          );
                        })
                        .map((v) => {
                          return (
                            <div key={v.id} className="pt-1 mt-3">
                              <p className="text-sm text-content-600 font-medium mb-2">
                                {verificationNameToTitle(
                                  v.name as VerificationNames
                                ) || v.name}
                              </p>
                              <span className="text-xs text-content-500 font-bold">
                                <VerificationBadge verification={v} />
                              </span>
                            </div>
                          );
                        })
                    : null}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {applicationId && manualApplicationOpen ? (
        <InviteAndCreateModal
          disabledRoles={
            [...applicants, ...pendingMagicLinks].some((applicant) =>
              [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                applicant.role as ApplicantType
              )
            )
              ? []
              : [ApplicantType.CoSigner]
          }
          key={manualApplicationOpen + " " + applicationId}
          applicantId={Number(manualApplicationOpen)}
          applicationId={Number(applicationId)}
          open={Boolean(manualApplicationOpen)}
          setOpen={() => {
            setManualApplicationOpen(null);
          }}
        />
      ) : null}

      <ActiveDiscountsModal
        isOpen={showActiveDiscountCodes}
        onClose={() => setShowActiveDiscountCodes(false)}
        discounts={availableDiscounts}
      />
    </>
  );
};
